import React, { useEffect, useState } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import Collapsible from "react-collapsible";
import "./modal.css"; // We will add improved styles here or inline
import { IoIosRefresh, IoMdClose, IoMdCreate, IoMdArrowBack, IoMdDownload, IoMdArrowRoundBack, IoMdArrowRoundForward } from "react-icons/io";
import { MdReply, MdMarkEmailRead, MdMarkEmailUnread, MdAttachFile, MdImage } from "react-icons/md";
import { CircularProgress, Tooltip, IconButton } from "@mui/material";
import { FaFilePdf } from "react-icons/fa";
import { decode } from "base64-arraybuffer";
import toast from "react-hot-toast";
import axios from "axios";



const EmailListItem = ({ email, theme, isDarkMode, isOpen, onToggle, renderContent }) => {
  const isRead = email.is_read || email.initial_state === "seen";
  const isReplied = email.is_replied;
  const isFlagged = email.is_flagged;

  const safeFrom = String(email.from || "");
  const displayFrom = email.from_name || (safeFrom ? safeFrom.replace(/<.*>/, '').replace(/"/g, '') : "Unknown Sender");

  return (
    <Collapsible
      open={isOpen}
      handleTriggerClick={onToggle}
      trigger={
        <div style={{
          display: "flex",
          flexDirection: "column",
          padding: "16px 20px",
          borderBottom: `1px solid ${theme.divider}`,
          backgroundColor: isRead ? theme.readBg : theme.unreadBg,
          cursor: "pointer",
          transition: "background 0.2s",
          position: "relative",
        }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.hover}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isRead ? theme.readBg : theme.unreadBg}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <div style={{
              fontWeight: isRead ? "500" : "700",
              fontSize: "15px",
              color: theme.text,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontFamily: theme.fontFamily,
              overflow: "hidden"
            }}>
              {!isRead && <div style={{ minWidth: "8px", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: theme.accent }}></div>}
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {displayFrom}
              </span>
              {isFlagged && <span style={{ fontSize: "10px", backgroundColor: "#ffc107", color: "#000", padding: "1px 4px", borderRadius: "3px" }}>★</span>}
            </div>
            <div style={{ fontSize: "12px", color: theme.secondaryText, fontFamily: theme.fontFamily, whiteSpace: "nowrap", marginLeft: "10px" }}>
              {email.date}
            </div>
          </div>

          <div style={{
            fontSize: "14px",
            color: isRead ? theme.secondaryText : theme.text,
            fontWeight: isRead ? "400" : "600",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "95%",
            fontFamily: theme.fontFamily
          }}>
            {email.subject || "(No Subject)"}
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: "8px", fontSize: "11px", color: theme.secondaryText }}>
            {isReplied && <span style={{ display: "flex", alignItems: "center", gap: "3px", color: "#4caf50", fontWeight: "600", border: "1px solid #4caf50", padding: "0 4px", borderRadius: "4px" }}><MdReply /> Replied</span>}
            <span style={{ backgroundColor: isDarkMode ? "#333" : "#eee", padding: "2px 6px", borderRadius: "4px", color: theme.secondaryText }}>
              {email.from_email || email.from.replace(/<.*>/, '').replace(/"/g, '').trim()}
            </span>
          </div>
        </div>
      }
      transitionTime={200}
    >
      {renderContent(email)}
    </Collapsible>
  );
};

const Inbox = ({ open2, handleClose, handleOpenReply, handleCompose, isDarkMode, selectedEmail, handleBack }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Naive check
  const [expandedEmailId, setExpandedEmailId] = useState(null);

  const handleToggleEmail = (email) => {
    const isExpanding = expandedEmailId !== email.email_id;
    setExpandedEmailId(isExpanding ? email.email_id : null);

    // Mark as seen if expanding and not already read
    if (isExpanding && !email.is_read && email.initial_state !== "seen") {
      handleMarkAsSeen(email);
    }
  };

  // Theme constants with better fonts
  const theme = {
    bg: isDarkMode ? "#1a1a1a" : "#ffffff",
    text: isDarkMode ? "#e0e0e0" : "#333333",
    secondaryText: isDarkMode ? "#aaaaaa" : "#666666",
    hover: isDarkMode ? "#2d2d2d" : "#f5f5f5",
    border: isDarkMode ? "#333333" : "#e0e0e0",
    accent: isDarkMode ? "#4dabf5" : "#007bff",
    cardBg: isDarkMode ? "#252525" : "#ffffff",
    readBg: isDarkMode ? "#1a1a1a" : "#ffffff",
    unreadBg: isDarkMode ? "#2c2c2c" : "#f8fbff",
    divider: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
  };

  const fetchEmails = async (pageNum = 1) => {
    setLoading(true);
    // setEmails([]); // Don't clear immediately to avoid flicker if we want, but better to clear for clean state
    if (pageNum === 1) setEmails([]);

    try {
      const url = selectedEmail
        ? `https://casualfootwears.com/api/get_inbox/?page=${pageNum}&email_address=${encodeURIComponent(selectedEmail)}`
        : `https://casualfootwears.com/api/get_inbox/?page=${pageNum}`;
      const respon = await axios.get(url);
      // Ensure data is array
      if (Array.isArray(respon.data)) {
        if (respon.data.length === 0 && pageNum > 1) {
          setHasMore(false);
          setPage(prev => prev - 1); // Go back if empty
          toast.error("No more emails");
        } else {
          setEmails(respon.data);
          setHasMore(respon.data.length === 10); // If < 10, likely end
        }
      } else {
        console.error("Inbox data is not an array:", respon.data);
        setEmails([]);
      }
      setLoading(false);
      // toast.success("Inbox updated");
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.error("Failed to fetch emails");
    }
  };

  useEffect(() => {
    if (open2) {
      setPage(1);
      fetchEmails(1);
    }
  }, [open2, selectedEmail]);

  const handleNextPage = () => {
    if (hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchEmails(nextPage);
    }
  }

  const handlePrevPage = () => {
    if (page > 1) {
      const prevPage = page - 1;
      setPage(prevPage);
      fetchEmails(prevPage);
    }
  }

  const handleMarkAsSeen = async (email) => {
    if (!email.is_read) {
      try {
        await axios.post('https://casualfootwears.com/api/set_mail_seen/', {
          uid: email.email_id,
          email_address: email.selected_mail
        });
        setEmails(prev => prev.map(e => e.email_id === email.email_id ? { ...e, is_read: true } : e));
      } catch (err) {
        console.error("Failed to mark flag", err);
      }
    }
  }

  const openPdfInNewTab = (base64pdf) => {
    const byteArray = decode(base64pdf);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const downloadImage = (base64image, filename) => {
    const byteArray = decode(base64image);
    const blob = new Blob([byteArray], { type: "image/*" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderAttachments = (email) => {
    if (!Array.isArray(email.attachments) || email.attachments.length === 0) return null;
    return (
      <div style={{ marginTop: "15px", borderTop: `1px solid ${theme.divider}`, paddingTop: "10px" }}>
        <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: theme.text }}>
          <MdAttachFile style={{ verticalAlign: "middle", marginRight: "5px" }} />
          Attachments ({email.attachments.length})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {email.attachments.map((attachment, index) => {
            const isImage = attachment.content_type?.startsWith("image/");
            const isPdf = attachment.content_type === "application/pdf" || attachment.filename?.endsWith(".pdf");

            return (
              <div key={index} style={{
                border: `1px solid ${theme.border}`,
                borderRadius: "6px",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: theme.bg,
                cursor: "pointer",
                fontSize: "12px",
                color: theme.text,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
              }}
                onClick={() => isPdf ? openPdfInNewTab(attachment.content) : isImage ? downloadImage(attachment.content, attachment.filename) : null}
              >
                {isPdf ? <FaFilePdf color="#d32f2f" /> : isImage ? <MdImage color="#1976d2" /> : <MdAttachFile />}
                <span style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {attachment.filename}
                </span>
                <IoMdDownload style={{ color: theme.secondaryText }} />
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  const renderEmailContent = (email, isReply = false) => (
    <div style={{
      padding: "20px",
      color: theme.text,
      backgroundColor: isReply ? (isDarkMode ? "#222" : "#f9f9f9") : "transparent",
      borderLeft: isReply ? `3px solid ${theme.accent}` : "none",
      marginTop: isReply ? "15px" : "0",
      fontFamily: theme.fontFamily
    }}>
      {/* Header Info */}
      {/* Header Info */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", fontFamily: theme.fontFamily }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>

          {/* Sender Name - Primary */}
          <div style={{ fontSize: "16px", fontWeight: "700", color: theme.text }}>
            {email.from_name || email.from.split('<')[0].replace(/"/g, '')}
          </div>

          {/* Details Line: From Email + To */}
          <div style={{ fontSize: "13px", color: theme.secondaryText, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            <span style={{ color: theme.text }}>&lt;{email.from_email || email.from}&gt;</span>

            <span style={{ fontSize: "10px", color: theme.divider }}>•</span>

            <span>To: <span style={{ color: theme.text }}>{(Array.isArray(email.to) ? email.to : []).join(", ")}</span></span>

            {Array.isArray(email.cc) && email.cc.length > 0 && (
              <>
                <span style={{ fontSize: "10px", color: theme.divider }}>•</span>
                <span>CC: {email.cc.join(", ")}</span>
              </>
            )}
          </div>
        </div>

        <div style={{ fontSize: "12px", color: theme.secondaryText, whiteSpace: "nowrap" }}>
          {email.date}
        </div>
      </div>

      {/* Body with safe Iframe */}
      <div
        className="email-body-container"
        style={{
          marginTop: "15px",
          borderRadius: "8px",
          border: `1px solid ${theme.border}`,
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <iframe
          title="email-content"
          srcDoc={
            "<!DOCTYPE html><html><head><style>body { font-family: 'Segoe UI', sans-serif; margin: 0; padding: 20px; color: #333; } img { max-width: 100%; height: auto; } a { color: #007bff; text-decoration: none; } ::-webkit-scrollbar { width: 8px; } ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }</style><base target='_blank'></head><body>" + (email.body || "") + "</body></html>"
          }
          style={{
            width: "100%",
            height: "400px",
            border: "none",
            display: "block"
          }}
          sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
        />
      </div>

      {renderAttachments(email)}

      {!isReply && email.selected_mail !== email.from && (
        <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={() => handleOpenReply(email)}
            style={{
              padding: "8px 20px",
              borderRadius: "20px",
              border: "none",
              backgroundColor: theme.accent,
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "600",
              fontSize: "14px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              transition: "transform 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
            onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
          >
            <MdReply size={18} /> Reply
          </button>
        </div>
      )}

      {/* Render Nested Replies */}
      {Array.isArray(email.replies) && email.replies.length > 0 && (
        <div style={{ marginTop: "20px", borderTop: `1px solid ${theme.divider}`, paddingTop: "20px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: theme.secondaryText, marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Previous Replies
          </div>
          {email.replies.map((reply) => (
            <Collapsible
              key={reply.email_id}
              lazyRender={true}
              trigger={
                <div style={{
                  padding: "12px",
                  backgroundColor: theme.hover,
                  borderRadius: "8px",
                  marginBottom: "10px",
                  cursor: "pointer",
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  border: `1px solid ${theme.border}`,
                  alignItems: "center"
                }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <strong style={{ color: theme.text }}>{reply.from_name || reply.from}</strong>
                    <span style={{ color: theme.secondaryText, fontSize: "12px" }}>{reply.subject}</span>
                  </div>
                  <span style={{ fontSize: "11px", color: theme.secondaryText }}>{reply.date}</span>
                </div>
              }
            >
              {renderEmailContent(reply, true)}
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );



  return (
    <Modal
      open={open2}
      onClose={handleClose}
      styles={{
        modal: {
          maxWidth: "900px",
          width: "95%",
          padding: "0",
          borderRadius: "16px",
          backgroundColor: theme.cardBg,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          fontFamily: theme.fontFamily
        },
        overlay: {
          background: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)"
        }
      }}
      center
      showCloseIcon={false}
    >
      {/* Modal Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: `1px solid ${theme.divider}`,
          backgroundColor: theme.bg
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: theme.text, letterSpacing: "-0.5px" }}>Inbox</h2>
          {loading && <CircularProgress size={24} style={{ color: theme.accent }} />}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Tooltip title="Previous Page">
            <span> {/* Wrapper for disabled state tooltip */}
              <IconButton onClick={handlePrevPage} disabled={page === 1} style={{ color: theme.text, opacity: page === 1 ? 0.3 : 1 }}>
                <IoMdArrowRoundBack />
              </IconButton>
            </span>
          </Tooltip>
          <span style={{ fontSize: "14px", color: theme.text, fontWeight: "600" }}>Page {page}</span>
          <Tooltip title="Next Page">
            <span>
              <IconButton onClick={handleNextPage} disabled={!hasMore} style={{ color: theme.text, opacity: !hasMore ? 0.3 : 1 }}>
                <IoMdArrowRoundForward />
              </IconButton>
            </span>
          </Tooltip>

          <div style={{ width: "1px", height: "24px", backgroundColor: theme.divider, margin: "0 8px" }}></div>

          <Tooltip title="Compose">
            <button onClick={handleCompose} className="icon-btn-premium" style={{ color: theme.text, backgroundColor: theme.hover }}>
              <IoMdCreate size={20} />
            </button>
          </Tooltip>
          <Tooltip title="Refresh">
            <button onClick={() => fetchEmails(1)} className="icon-btn-premium" style={{ color: theme.text, backgroundColor: theme.hover }}>
              <IoIosRefresh size={20} className={loading && "spin-animation"} />
            </button>
          </Tooltip>
          <Tooltip title="Back">
            <button onClick={handleBack} className="icon-btn-premium" style={{ color: theme.text, backgroundColor: theme.hover }}>
              <IoMdArrowBack size={20} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Email List */}
      <div style={{ maxHeight: "70vh", overflowY: "auto", minHeight: "400px", backgroundColor: theme.bg }}>
        {loading && emails.length === 0 ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
            <CircularProgress style={{ color: theme.accent }} />
          </div>
        ) : (!Array.isArray(emails) || emails.length === 0) ? (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "400px", color: theme.secondaryText }}>
            <MdMarkEmailRead size={64} style={{ opacity: 0.2, marginBottom: "15px" }} />
            <p style={{ fontSize: "16px", fontWeight: "500" }}>All clean!</p>
          </div>
        ) : (
          emails.map((email) => (
            <EmailListItem
              key={email.email_id}
              email={email}
              theme={theme}
              isDarkMode={isDarkMode}
              isOpen={expandedEmailId === email.email_id}
              onToggle={() => handleToggleEmail(email)}
              renderContent={renderEmailContent}
            />
          ))
        )}
      </div>

      {/* Inline styles */}
      <style>{`
          .icon-btn-premium {
              border: none;
              outline: none;
              width: 36px;
              height: 36px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .icon-btn-premium:hover {
              transform: scale(1.1);
              background-color: ${theme.hover};
          }
           .spin-animation {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
             0% { transform: rotate(0deg); }
             100% { transform: rotate(360deg); }
          }
          
           /* Scrollbar Beautification */
            ::-webkit-scrollbar {
            width: 8px;
            }
            ::-webkit-scrollbar-track {
            background: transparent; 
            }
            ::-webkit-scrollbar-thumb {
            background: ${isDarkMode ? "#444" : "#ccc"}; 
            border-radius: 4px;
            }
            ::-webkit-scrollbar-thumb:hover {
            background: ${isDarkMode ? "#555" : "#bbb"}; 
            }
      `}</style>

    </Modal>
  );
}

export default Inbox;
